import { getVariantSelectors } from '../../utils/variantsUtils'
import type { ContainerQueryToSelectorObj } from '../../shared.types'
import type { DomApplier } from '../cssFeatures.types'
import type { CssContext } from '../componentCss.types'
import type { CompVariantsViewItem } from '@wix/thunderbolt-catharsis'

// default params
const BG_SKEW_ANGLE = 20
const BG_ROTATE_ANGLE = 22
const BG_PAN_SPEED = 0.2
const IMAGE_PARALLAX_SPEED = 1.5

const FULL_SCREEN_HEIGHT_STR = 'max(100lvh, 100%)'
const EFFECT_TO_INITIAL_SIZE_STYLING = {
	BgParallax: (_params: any) => ({ height: FULL_SCREEN_HEIGHT_STR }),
	BgReveal: (_params: any) => ({ height: FULL_SCREEN_HEIGHT_STR }),
	BgFake3D: (_params: any) => ({ height: FULL_SCREEN_HEIGHT_STR }),
	BgSkew: (params: any) => ({
		'padding-bottom': `${(100 * Math.tan(((params.angle || BG_SKEW_ANGLE) * Math.PI) / 180)).toFixed(3)}%`,
		'margin-top': `${(-50 * Math.tan(((params.angle || BG_SKEW_ANGLE) * Math.PI) / 180)).toFixed(3)}%`,
	}),
	BgRotate: (params: any) => ({
		width: `max(100%, calc(${(100 * Math.cos(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(
			3
		)}cqw + ${(100 * Math.sin(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(3)}cqh))`,
		height: `max(100%, calc(${(100 * Math.sin(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(
			3
		)}cqw + ${(100 * Math.cos(((params.angle || BG_ROTATE_ANGLE) * Math.PI) / 180)).toFixed(3)}cqh))`,
		translate: 'calc(50cqw - 50%) calc(50cqh - 50%)',
	}),
	BgPan: (params: any) => ({ width: `${100 * (1 + (params.speed || BG_PAN_SPEED))}%` }),
	BgZoom: (params: any) =>
		params.direction === 'in' ? { height: FULL_SCREEN_HEIGHT_STR } : { width: '115%', height: '115%' },
	ImageParallax: (params: any) => ({ height: `${100 * (params.speed || IMAGE_PARALLAX_SPEED)}%` }),
}

const EFFECT_TO_INITIAL_WOW_STYLING = {
	BgPan: (params: any) => ({
		left: `${(-50 * ((params.speed || BG_PAN_SPEED) / (1 + (params.speed || BG_PAN_SPEED)))).toFixed(3)}%`,
	}),
	BgZoom: (params: any) =>
		params.direction === 'in' ? {} : { top: `-${(750 / 115).toFixed(3)}%`, left: `-${(750 / 115).toFixed(3)}%` },
}

function transformToFrameSelector(selectors: Array<string>) {
	return selectors.map((selector_) => `${selector_}`).join(',')
}

function getBgRotateContainerRules(angle: number, bgMediaSelector: string) {
	const containerRules: ContainerQueryToSelectorObj = {}
	const angleInRad = (angle * Math.PI) / 180
	const tanAngle = Math.tan(angleInRad).toFixed(3)
	const cotAngle = Math.tan(((90 - angle) * Math.PI) / 180).toFixed(3)
	const minAspectRatioQuery = `@container(min-aspect-ratio: ${cotAngle})`
	const maxAspectRatioQuery = `@container(max-aspect-ratio: ${tanAngle})`
	const width = `calc(100% + min(${(100 * (Math.sqrt(1 + Math.pow(Math.tan(angleInRad), 2)) - 1)).toFixed(3)}%, ${(
		100 * Math.sin(angleInRad)
	).toFixed(3)}cqh))`
	const height = `calc(100% + min(${(100 * (Math.sqrt(1 + Math.pow(Math.tan(angleInRad), 2)) - 1)).toFixed(3)}%, ${(
		100 * Math.sin(angleInRad)
	).toFixed(3)}cqw))`
	containerRules[minAspectRatioQuery] = {}
	containerRules[maxAspectRatioQuery] = {}
	containerRules[minAspectRatioQuery][bgMediaSelector] = { width }
	containerRules[maxAspectRatioQuery][bgMediaSelector] = { height }
	return containerRules
}

function getStyleRulesByEffect(
	namedEffect: any,
	compId: string,
	{
		variantKey,
		variants,
		cssContext,
	}: { variantKey: string; variants: CompVariantsViewItem | null; cssContext: CssContext }
) {
	const styleRules: Record<string, {}> = {}
	const bgMediaSelector = `${transformToFrameSelector(
		getVariantSelectors(variantKey, variants, compId, cssContext)
	)} [data-motion-part="BG_MEDIA"]`
	if (!namedEffect || !namedEffect.type) {
		styleRules[bgMediaSelector] = { height: '100%', width: '100%' }
		return styleRules
	}
	const wowImageSelector = `${bgMediaSelector} wow-image`
	const name = namedEffect?.type as keyof typeof EFFECT_TO_INITIAL_SIZE_STYLING
	const sizeStyleMapper = EFFECT_TO_INITIAL_SIZE_STYLING[name]

	if (sizeStyleMapper) {
		styleRules[bgMediaSelector] = sizeStyleMapper(namedEffect)
	}
	const wowStyleMapper = EFFECT_TO_INITIAL_WOW_STYLING[name as keyof typeof EFFECT_TO_INITIAL_WOW_STYLING]
	if (wowStyleMapper) {
		styleRules[wowImageSelector] = wowStyleMapper(namedEffect)
	}
	if (name === 'BgRotate') {
		const bgLayersSelector = `${transformToFrameSelector(
			getVariantSelectors(variantKey, variants, compId, cssContext)
		)} [data-motion-part="BG_LAYER"]`
		styleRules[bgLayersSelector] = { 'container-type': 'size' }
		Object.assign(styleRules, getBgRotateContainerRules(namedEffect.angle || BG_ROTATE_ANGLE, bgMediaSelector))
	}
	return styleRules
}

export const motionCssDomApplier: DomApplier<'motion'> = (
	{ id: compId },
	idSelector,
	breakpointId,
	{ motion },
	cssContext
) => {
	if (motion) {
		// fetches initial frame from all effects per a given breakpointId
		return motion
			.flatMap(({ data: breakpointToEffect, variants }) => {
				const { default: defaultBreakpoint, ...restOfBreakpoints } = breakpointToEffect

				const defaultRules = Object.entries(defaultBreakpoint).map(([variantKey, item]) =>
					getStyleRulesByEffect(item?.namedEffect, compId, { variantKey, variants, cssContext })
				)
				if (breakpointId === 'default') {
					return defaultRules
				}

				return restOfBreakpoints[breakpointId]
					? Object.entries(restOfBreakpoints[breakpointId]).map(([variantKey, item]) =>
							getStyleRulesByEffect(item?.namedEffect, compId, { variantKey, variants, cssContext })
					  )
					: []
			})
			.reduce((acc, currentStyleRules) => {
				Object.keys(currentStyleRules).forEach((selector) => {
					if (!acc[selector]) {
						acc[selector] = {}
					}

					Object.assign(acc[selector], currentStyleRules[selector])
				})
				return acc
			}, {} as Record<string, {}>)
	}

	return null
}
